import {inject, NgModule} from '@angular/core';
import {Router, RouterModule, Routes} from '@angular/router';
import {
  ACCOUNT_LOGIN_ROUTE,
  CHANGE_PASSWORD_ROUTE,
  CREATE_ACCOUNT_ROUTE,
  DESTINATION_KEY_CONSTANT,
  EMAIL_KEY_CONSTANT,
  EMAIL_VERIFICATION_COMPLETE_ROUTE,
  EMAIL_VERIFICATION_CTA_ROUTE,
  EM,
  LOGIN_OBJECT_KEY_CONSTANT,
  LOGIN_SUCCESSFUL_ROUTE,
  PRODUCT_KEY_CONSTANT,
  RESET_PASSWORD_ROUTE,
  SIGNOUT_ROUTE,
  VERIFY_EMAIL_ROUTE,
  CONTINUE_WITH_GOOGLE_SIGN_UP_ROUTE,
  CONTINUE_WITH_GOOGLE_SIGN_IN_ROUTE,
  CONTINUE_WITH_APPLE_SIGN_IN_ROUTE, CONTINUE_WITH_APPLE_SIGN_UP_ROUTE,
} from './constants/routes.constant';
import {CreateAccountComponent} from './create-account/create-account.component';
import {LoginComponent} from './login/login.component';
import {EmailVerificationCtaComponent} from './email-verification-cta/email-verification-cta.component';
import {EmailVerificationCompleteComponent} from './email-verification-complete/email-verification-complete.component';
import {VerifyEmailComponent} from './verify-email/verify-email.component';
import {ResetPasswordComponent} from './reset-password/reset-password.component';
import {ChangePasswordComponent} from './change-password/change-password.component';
import {LoginSuccessfulComponent} from './login-successful/login-successful.component';
import {SignoutComponent} from './signout/signout.component';
import {ONBOARDING_ID_TOKEN_KEY, SESSION_TOKEN_KEY,} from './constants/local-storage.constant';
import {UserProfileService} from "./services/user-profile.service";
import {lastValueFrom} from "rxjs";
import {CreateWithGoogleComponent} from "./create-with-google/create-with-google.component";
import {SignInWithGoogleComponent} from "./sign-in-with-google/sign-in-with-google.component";
import {SignInWithAppleComponent} from "./sign-in-with-apple/sign-in-with-apple.component";
import {CreateWithAppleComponent} from "./create-with-apple/create-with-apple.component";

const getQueryParams = () => {
  const searchParams = new URL(location.href).searchParams;

  return {
    [DESTINATION_KEY_CONSTANT]: searchParams.get(DESTINATION_KEY_CONSTANT),
    [PRODUCT_KEY_CONSTANT]: searchParams.get(PRODUCT_KEY_CONSTANT),
    [LOGIN_OBJECT_KEY_CONSTANT]: searchParams.get(LOGIN_OBJECT_KEY_CONSTANT),
  };
};

const canActivateVerifyEmailCtaFn = () => {
  return !!localStorage.getItem(ONBOARDING_ID_TOKEN_KEY);
};

export const canActivateLoginFn = async () => {
  const router = inject(Router);
  const queryParams = getQueryParams();
  const userProfileService = inject(UserProfileService);

  try {
    const result = await lastValueFrom(userProfileService.getMyProfileDetails());

    if (!result) {
      return Promise.resolve(true);
    }

    return router.navigate(['/', ...LOGIN_SUCCESSFUL_ROUTE.split('/')], { queryParams });
  } catch (error) {
    console.error('ERROR in guard o', error);

    return Promise.resolve(true);
  }
};

const canOpenSignOutFn = async () => {
  const router = inject(Router);
  const queryParams = getQueryParams();
  const userProfileService = inject(UserProfileService);

  try {
    const result = await lastValueFrom(userProfileService.getMyProfileDetails());

    if (!result) {
      return router.navigate([ACCOUNT_LOGIN_ROUTE], { queryParams });
    }

    return true;
  } catch (error) {
    console.error('ERROR in guard o', error);

    return router.navigate([ACCOUNT_LOGIN_ROUTE], { queryParams });
  }
};

const routes: Routes = [
  {
    title: 'Sign out - AshGridX',
    pathMatch: 'full',
    path: SIGNOUT_ROUTE,
    canActivate: [canOpenSignOutFn],
    component: SignoutComponent,
  },
  {
    title: 'Login Successful - AshGridX',
    pathMatch: 'full',
    path: LOGIN_SUCCESSFUL_ROUTE,
    component: LoginSuccessfulComponent,
  },
  {
    title: 'Change Password | Password Reset - AshGridX',
    pathMatch: 'full',
    path: CHANGE_PASSWORD_ROUTE + '/:' + EMAIL_KEY_CONSTANT,
    component: ChangePasswordComponent,
  },
  {
    title: 'Reset Password | Password Reset - AshGridX',
    pathMatch: 'full',
    path: RESET_PASSWORD_ROUTE,
    component: ResetPasswordComponent,
  },
  {
    title: 'Verify Email | Email Verification - AshGridX',
    pathMatch: 'full',
    path: `${VERIFY_EMAIL_ROUTE}/:` + EM,
    component: VerifyEmailComponent,
  },
  {
    title: 'Verified | Email Verification - AshGridX',
    pathMatch: 'full',
    path: EMAIL_VERIFICATION_COMPLETE_ROUTE,
    component: EmailVerificationCompleteComponent,
  },
  {
    title: 'Check | Email Verification - AshGridX',
    pathMatch: 'full',
    path: EMAIL_VERIFICATION_CTA_ROUTE,
    canActivate: [canActivateVerifyEmailCtaFn],
    component: EmailVerificationCtaComponent,
  },
  {
    title: 'Check | Email Verification - AshGridX',
    pathMatch: 'full',
    path: EMAIL_VERIFICATION_CTA_ROUTE + '/:' + EM,
    canActivate: [canActivateVerifyEmailCtaFn],
    component: EmailVerificationCtaComponent,
  },
  {
    title: 'Log In with Apple - AshGridX',
    pathMatch: 'full',
    path: CONTINUE_WITH_APPLE_SIGN_IN_ROUTE,
    component: SignInWithAppleComponent,
  },
  {
    title: 'Log In with Google - AshGridX',
    pathMatch: 'full',
    path: CONTINUE_WITH_GOOGLE_SIGN_IN_ROUTE,
    component: SignInWithGoogleComponent,
  },
  {
    title: 'Log In - AshGridX',
    pathMatch: 'full',
    path: ACCOUNT_LOGIN_ROUTE,
    canActivate: [canActivateLoginFn],
    component: LoginComponent,
  },
  {
    title: 'Create Account with Apple - AshGridX',
    pathMatch: 'full',
    path: CONTINUE_WITH_APPLE_SIGN_UP_ROUTE,
    component: CreateWithAppleComponent,
  },
  {
    title: 'Create Account with Google - AshGridX',
    pathMatch: 'full',
    path: CONTINUE_WITH_GOOGLE_SIGN_UP_ROUTE,
    component: CreateWithGoogleComponent,
  },
  {
    title: 'Create Account - AshGridX',
    pathMatch: 'full',
    path: CREATE_ACCOUNT_ROUTE,
    component: CreateAccountComponent,
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: `/${CREATE_ACCOUNT_ROUTE}`,
  },
  {
    path: '**',
    redirectTo: '/',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: 'always',
      enableTracing: false,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
